import { createAction, props } from '@ngrx/store';
import {
  Workflow,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepRule,
  WorkflowStepRuleAction,
  View,
  ViewComponent,
  WorkflowRuleImplementation
} from './workflow.model';
import { QueryParams, QueryResult } from '../../../core/store/models/query.model';
import { SchemaField } from '../data';

//
// Workflow Page actions
//
export const setSelectedWorkflow = createAction('[Workflow/Page] Set Selected Workflow', props<{ workflow: Workflow }>());
export const setUpdatedWorkflow = createAction('[Workflow/Page] Set Updated Workflow', props<{ workflow: Workflow }>());
export const configureWorkflow = createAction('[Workflow/Page] Configure Workflow');
export const editWorkflow = createAction('[Workflow/Page] Edit Workflow', props<{ workflowUId?: string }>());
export const editSelectedWorkflow = createAction('[Workflow/Page] Edit Selected Workflow');
export const setWorkflowStepForDelete = createAction(
  '[Workflow/Page] Set Workflow Step for Delete',
  props<{ workflowStep: WorkflowStep }>()
);
export const setWorkflowStepsForDeleteComplete = createAction(
  '[Workflow/API] Get Workflow Steps For Deletion Complete',
  props<{ deleteStep: WorkflowStep; results: WorkflowStep[] }>()
);
export const clearWorkflowStepForDelete = createAction('[Workflow/Page] Clear Workflow Step for Delete');
export const setWorkflowActionForDelete = createAction(
  '[Workflow/Page] Set Workflow Action for Delete',
  props<{ workflowStep: WorkflowStep; action: WorkflowStepAction }>()
);
export const clearWorkflowStepActionForDelete = createAction('[Workflow/Page] Clear Workflow Action for Delete');
export const setError = createAction('[Workflow/Page] Set Error', props<{ err: any }>());
export const setIsWorking = createAction('[Workflow/Page] Is Working', props<{ isWorking: boolean }>());

//
// Workflow API actions
//
export const getWorkflow = createAction('[Workflow/API] Get Workflow', props<{ workflowUId: string }>());
export const getWorkflowForDataset = createAction('[Workflow/API] Get Workflow For Dataset', props<{ datasetUId: string; skipPagesValidation?: boolean }>());
export const getWorkflowForDatasetComplete = createAction(
  '[Workflow/API] Get Workflow For Dataset Complete',
  props<{ workflow: Workflow; skipPagesValidation?: boolean }>()
);
export const saveSelectedWorkflow = createAction('[Workflow/API] Save Selected Workflow');
export const updateWorkflow = createAction('[Workflow/API] Update Workflow', props<{ workflow: Workflow }>());
export const updateWorkflowComplete = createAction('[Workflow/API] Update Workflow Complete', props<{ workflow: Workflow }>());

//
// Workflow Step API actions
//
export const getWorkflowSteps = createAction('[Workflow/API] Get Workflow Steps', props<{ workflowUId: string }>());
export const getWorkflowStepsComplete = createAction('[Workflow/API] Get Workflow Steps', props<{ results: QueryResult<WorkflowStep> }>());
export const addWorkflowStep = createAction('[Workflow/API] Add Workflow Step');
export const updateWorkflowStep = createAction('[Workflow/API] Update Workflow Step', props<{ workflowStep: WorkflowStep }>());
export const deleteWorkflowStep = createAction('[Workflow/API] Delete Workflow Step');
export const moveWorkflowStep = createAction(
  '[Workflow/API] Move Workflow Step',
  props<{
    previousIndex: number;
    currentIndex: number;
  }>()
);
export const setWorkflowStepMigration = createAction('[Workflow/API] Migrate Workflow Steps on Save', props<{ stepUId: string }>());

//
// Workflow Step Action actions
//
export const addWorkflowStepAction = createAction('[Workflow/API] Add Workflow Step Action', props<{ workflowStep: WorkflowStep }>());
export const updateWorkflowStepAction = createAction(
  '[Workflow/API] Update Workflow Step Action',
  props<{ workflowStep: WorkflowStep; stepAction: WorkflowStepAction }>()
);
export const deleteWorkflowStepAction = createAction('[Workflow/API] Delete Workflow Step Action');
export const moveWorkflowStepAction = createAction(
  '[Workflow/API] Move Workflow Step Action',
  props<{
    workflowStep: WorkflowStep;
    previousIndex: number;
    currentIndex: number;
  }>()
);
export const addEditTransitionForm = createAction(
  '[Workflow/Page] Add Edit Transition Form',
  props<{ workflowStep: WorkflowStep; stepAction: WorkflowStepAction }>()
);
export const cancelAddEditTransitionForm = createAction('[Workflow/Page] Cancel Add Edit Transition Form');

export const addEditDetailsForm = createAction('[Workflow/Page] Add Edit Details Form', props<{ workflowStep: WorkflowStep }>());
export const cancelEditDetailsForm = createAction('[Workflow/Page] Cancel Edit Details Form');
export const saveAddEditDetailsForm = createAction('[Workflow/Page] Save Add Edit Details Form', props<{ updatedView: View }>());

//
// View actions
//
export const setSelectedView = createAction('[View/Page] Set Selected View', props<{ view: View }>());
export const getView = createAction('[View/API] Get View', props<{ viewUId: string }>());
export const saveAddEditTransitionForm = createAction('[View/API] Save Add Edit Transition Form', props<{ updatedView: View }>());
export const deleteViewComponent = createAction('[View/API] Delete View Component', props<{ component: ViewComponent }>());
export const dragDropComponent = createAction('[View/API] Drag Drop Component', props<{ previousIndex: number; currentIndex: number }>());
export const editView = createAction('[View/API] EditView', props<{ view: View; name?: string }>());
export const moveComponent = createAction(
  '[View/API] Move Component',
  props<{
    view: View;
    selectedViewControlContainerUId?: string;
    previousIndex: number;
    currentIndex: number;
  }>()
);

export const setIsDirty = createAction('[View/API] Set Is Workflow Dirty', props<{ isDirty: boolean }>());

export const addWorkflowStepRule = createAction('[Workflow/Page] Add Workflow Step Rule', props<{ step: WorkflowStep }>());
export const editWorkflowStepRule = createAction(
  '[Workflow/Page] Edit Workflow Step Rule',
  props<{ step: WorkflowStep; rule: WorkflowStepRule }>()
);
export const setWorkflowStepRuleForDelete = createAction(
  '[Workflow/Page] Set Workflow Step Rule for Delete',
  props<{ step: WorkflowStep; rule: WorkflowStepRule }>()
);
export const clearWorkflowStepRuleForDelete = createAction('[Workflow/Page] Clear Workflow Step Rule for Delete');
export const deleteSelectedWorkflowStepRule = createAction('[Workflow/Page] Delete Selected Workflow Step Rule');
export const setSelectedWorkflowStepRule = createAction(
  '[Workflow/Page] Set Selected Workflow Step Rule',
  props<{ step: WorkflowStep; rule: WorkflowStepRule }>()
);
export const clearSelectedWorkflowStepRule = createAction('[Workflow/Page] Clear Selected Workflow Step Rule');
export const loadWorkflowRuleImplementations = createAction('[Workflow/Page] Load Rule Implementations');
export const loadWorkflowRuleImplementationsComplete = createAction(
  '[Workflow/Page] Load Rule Implementations Complete',
  props<{ ruleImplementations: WorkflowRuleImplementation[] }>()
);
export const saveWorkflowStepRule = createAction(
  '[Workflow/Page] Save Workflow Rule',
  props<{ step: WorkflowStep; rule: WorkflowStepRule }>()
);
export const moveWorkflowStepRule = createAction(
  '[Workflow/API] Move Workflow Step Rule',
  props<{ workflowStep: WorkflowStep; previousIndex: number; currentIndex: number }>()
);

export const addWizardView = createAction('[Workflow/Page] Add Wizard View', props<{ view: View }>());
export const deleteWizardView = createAction('[Workflow/Page] Delete Wizard View', props<{ view: View }>());
export const moveWizardView = createAction('[Workflow/Page] Move Wizard View', props<{ previousIndex: number; currentIndex: number }>());
